import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const JornadaContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--color-dark);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h1)`
  color: var(--color-white);
  font-size: 3rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: var(--color-primary);
    border-radius: 3px;
  }
`;

const TimelineContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  position: relative;
  margin: 2rem 0;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    width: 4px;
    height: 100%;
    background: var(--color-primary);
    transform: translateX(-50%);
    border-radius: 2px;
    box-shadow: 0 0 10px rgba(255, 144, 82, 0.3);
  }
  
  @media (max-width: 768px) {
    &::before {
      left: 30px;
    }
  }
`;

const TimelineItem = styled(motion.div)<{ isLeft: boolean }>`
  width: 100%;
  display: flex;
  justify-content: ${props => props.isLeft ? 'flex-start' : 'flex-end'};
  margin-bottom: 4rem;
  position: relative;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    padding-left: 60px;
  }
`;

const TimelineContent = styled(motion.div)`
  width: calc(50% - 50px);
  background: rgba(26, 26, 26, 0.7);
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(10px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    width: 20px;
    height: 20px;
    background: var(--color-primary);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(255, 144, 82, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    
    &::before {
      left: -45px;
    }
  }
`;

const LeftContent = styled(TimelineContent)`
  &::before {
    right: -60px;
  }
  
  @media (max-width: 768px) {
    &::before {
      left: -45px;
      right: auto;
    }
  }
`;

const RightContent = styled(TimelineContent)`
  &::before {
    left: -60px;
  }
`;

const TimelineDate = styled.div`
  color: var(--color-primary);
  font-weight: bold;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`;

const TimelineTitle = styled.h3`
  color: var(--color-white);
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const TimelineCompany = styled.h4`
  color: var(--color-white);
  opacity: 0.8;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const TimelineDescription = styled.p`
  color: var(--color-white);
  opacity: 0.7;
  line-height: 1.6;
`;

const BackButton = styled(motion.button)`
  background: rgba(26, 26, 26, 0.7);
  color: var(--color-white);
  font-size: 1rem;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  border: 2px solid var(--color-primary);
  margin-top: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 144, 82, 0.2);
  }
`;

const Jornada: React.FC<{ onBack: () => void }> = ({ onBack }) => {
 const timelineItems = [
  {
    date: "Sep 2024 - Present",
    title: "Software Engineer",
    company: "ExitLag",
    description: "Leading the development of a new backend structure and building a microservices architecture."
  },
  {
    date: "Apr 2023 - Sep 2024",
    title: "Software Engineer",
    company: "Jostens",
    description: "Migrating and maintaining an ecommerce platform built in Shopify to a new architecture using React, nodejs with nest.js and microsservices for both ecommerce platform and admin panel."
  },
  {
    date: "Oct 2022 - Apr 2023",
    title: "Software Engineer",
    company: "Softensity",
    description: "Development of Next.js applications, and legacy system migrations. Technical leadership in architectural decisions and project estimations."
  },
  {
    date: "Dec 2021 - Sep 2024",
    title: "Software Architect Manager",
    company: "CI&T",
    description: "Technical leadership in architectural decisions and project estimations for 5 squads, team training and development, implementation of micro-frontend architectures, and legacy system migrations."
  },
  {
    date: "Apr 2021 - Dec 2021",
    title: "Tech Lead",
    company: "Dextra",
    description: "Technical leadership, architectural decisions, and estimations for 5 teams. Development and migration of micro-frontend-based architectures, along with technical mentorship of developers."
  },
  {
    date: "Aug 2020 - Apr 2021",
    title: "Senior Software Engineer",
    company: "PagSeguro PagBank",
    description: "Developed the PIX instant payment solution using Kotlin/Spring and led the creation of a recurring payments portal with Next.js and React.js."
  },
  {
    date: "Mar 2018 - Aug 2020",
    title: "Frontend Engineer",
    company: "Dextra Digital",
    description: "Projects for Globo.com and Nextel using technologies like Go, Node.js, React, AngularJS, AWS (Lambda, RDS, EC2, SQS), Redis, MySQL, PostgreSQL, and MongoDB."
  },
  {
    date: "Oct 2017 - Mar 2018",
    title: "Frontend Engineer",
    company: "Assertiva",
    description: "Frontend development with React.js, Redux, React Native, and Node.js. Implementation with MySQL databases."
  },
  {
    date: "Nov 2016 - Oct 2017",
    title: "Software Engineer",
    company: "agendaOS",
    description: "Development using React.js, React Native, Node.js, and PHP, integrated with MySQL."
  }
];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 12
      }
    }
  };

  return (
    <JornadaContainer>
      <Title
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        My Career
      </Title>
      
      <TimelineContainer
        as={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {timelineItems.map((item, index) => (
          <TimelineItem 
            key={index} 
            isLeft={index % 2 === 0}
            variants={itemVariants}
          >
            {index % 2 === 0 ? (
              <LeftContent>
                <TimelineDate>{item.date}</TimelineDate>
                <TimelineTitle>{item.title}</TimelineTitle>
                <TimelineCompany>{item.company}</TimelineCompany>
                <TimelineDescription>{item.description}</TimelineDescription>
              </LeftContent>
            ) : (
              <RightContent>
                <TimelineDate>{item.date}</TimelineDate>
                <TimelineTitle>{item.title}</TimelineTitle>
                <TimelineCompany>{item.company}</TimelineCompany>
                <TimelineDescription>{item.description}</TimelineDescription>
              </RightContent>
            )}
          </TimelineItem>
        ))}
      </TimelineContainer>
      
      <BackButton 
        onClick={onBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Voltar ao Menu
      </BackButton>
    </JornadaContainer>
  );
};

export default Jornada;
